// context-owner — o contexto de conta do store pertence à conta da sessão Swipe, e a
// nenhuma outra (cybersec P1, 10/10/2026).
//
// ── O defeito ──
// O contexto de sync (fonte, usuário, perfil ativo, e por eles o escopo do local-db)
// sobrevivia à sessão Swipe. Caminhos reproduzidos com peças reais:
//  (a) o 401 do refresh apaga só a sessão; a conta B montava o app com o perfil e o
//      "Continuar" de A (e, se a hidratação de B falhasse, o perfil de A ficava);
//  (b) logout com o bootstrap do mobile em voo regravava a conta e o perfil de A;
//  (c) a resposta da hidratação de A chegava depois da limpeza e devolvia A;
//  R2-A / R2-A2 (rodada 3): o contexto confirmado FORA da hidratação pela conta (tela
//      do código, fonte manual, lista direta, relogin por `/login` depois de um
//      `logout('session_expired')`) nascia SEM dono, e sem dono o 401 não o derrubava:
//      B herdava o perfil de A, e na TV o "parear sem fonte + Voltar" caía em `/home`
//      com a sessão Xtream e o perfil de A.
// (b) e (c) são da epoch do bootstrap (mobile-sync-bootstrap.ts). Este módulo fecha o
// resto: TODO contexto confirmado com sessão viva ganha dono, e o contexto cai no
// instante em que a sessão deixa de ser do dono — de forma síncrona, antes do render.
//
// ── As três peças ──
//  1. DONO EM TODA CONFIRMAÇÃO, num lugar só (`bindContextOwner`, assinatura do store):
//     quando um contexto é confirmado (`isAuthed` vira true ou `username` deixa de ser
//     nulo) sem dono e com sessão viva, o dono é a conta da sessão. Quem confirma não
//     precisa lembrar de gravar (as três telas de `setAuth` e o relogin não lembravam).
//  2. TROCA DE IDENTIDADE da sessão (assinatura em swipe-session.ts: 401 definitivo,
//     sign-out, login de outra conta): dono registrado e diferente → derruba.
//  3. BOOT (bootstrap.ts, antes de o React montar): além do item 2, a MIGRAÇÃO ÚNICA do
//     contexto sem dono de instalações anteriores a este campo, separada pelo marcador
//     `iptv:swipe-linked` (o aparelho já vinculou conta?):
//       - sem dono, COM sessão  → adota a sessão como dona (não derruba o perfil de todo
//         aparelho na atualização);
//       - sem dono, SEM sessão, COM marcador → derruba (a sessão da conta que vinculou
//         morreu com o app fechado; o contexto é dela);
//       - sem dono, sem sessão, SEM marcador → mantém: é a base só Xtream, que adota a
//         conta quando vincular (a hidratação do pareamento grava o dono).
//     Fora do boot, dono nulo NUNCA é adotado por uma troca de identidade; e, com o
//     marcador presente, é DERRUBADO (rodada 4: a sessão de A morrendo durante o /login).

import { useSessionStore } from './store'
import { wasSwipeLinked } from './swipe-linked-flag'
import { getSwipeUserId, onSwipeIdentityChange } from './sync'

export type ContextOwnerVerdict = 'kept' | 'adopted' | 'dropped'

type SessionState = ReturnType<typeof useSessionStore.getState>

/** Há contexto de conta no store pra alguém ser dono? */
function hasAccountContext(s: SessionState): boolean {
  return s.username !== null || s.activeProfileId !== null || s.isAuthed
}

/**
 * Confere o dono do contexto contra a conta da sessão (`currentUser`).
 *  - mesmo dono → `kept`;
 *  - dono registrado e a sessão é OUTRA (ou nenhuma) → `dropped`: o contexto inteiro cai;
 *  - dono nulo: só o `phase: 'boot'` decide (a migração única do cabeçalho); numa troca de
 *    identidade, `kept` sem adotar.
 */
export function reconcileContextOwner(
  currentUser: string | null = getSwipeUserId(),
  phase: 'boot' | 'identity' = 'boot',
): ContextOwnerVerdict {
  const s = useSessionStore.getState()
  const owner = s.contextOwner
  // Dono nulo primeiro: "sem dono e sem sessão" também é `owner === currentUser` (null),
  // e é justamente um dos casos da migração do boot.
  if (owner === null) {
    if (!hasAccountContext(s)) return 'kept'
    if (phase !== 'boot') {
      // Troca de identidade com contexto SEM dono num aparelho que JÁ vinculou conta
      // (rodada 4, P2 residual): a sessão de A morreu durante o `/login`, o `setAuth`
      // chegou sem sessão (sem dono) e B pareou sem reiniciar o app. Esse contexto é de
      // quem vinculou antes, nunca de B: derruba. Num PRIMEIRO vínculo o marcador ainda
      // não existe no instante do aviso (o `markSwipeLinked` roda depois de
      // `setSwipeSessionFromPairing`), então a base instalada só Xtream segue migrando.
      if (wasSwipeLinked()) {
        s.dropAccountContext()
        return 'dropped'
      }
      return 'kept'
    }
    if (currentUser !== null) {
      s.setContextOwner(currentUser)
      return 'adopted'
    }
    if (wasSwipeLinked()) {
      s.dropAccountContext()
      return 'dropped'
    }
    return 'kept'
  }
  if (owner === currentUser) return 'kept'
  s.dropAccountContext()
  return 'dropped'
}

/** A transição de `prev` pra `s` CONFIRMOU um contexto (login, relogin, hidratação)? */
function confirmedContext(s: SessionState, prev: SessionState): boolean {
  return (s.isAuthed && !prev.isAuthed) || (s.username !== null && prev.username === null)
}

let bound = false

/**
 * Liga as duas assinaturas: a troca de identidade da sessão (derruba o contexto alheio)
 * e a confirmação de contexto no store (grava o dono). Idempotente.
 */
export function bindContextOwner(): void {
  if (bound) return
  bound = true
  onSwipeIdentityChange((user) => {
    reconcileContextOwner(user, 'identity')
  })
  useSessionStore.subscribe((s, prev) => {
    if (s.contextOwner !== null || !confirmedContext(s, prev)) return
    const user = getSwipeUserId()
    if (user !== null) s.setContextOwner(user)
  })
}
